import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { UtilityTopbar } from "@/components/qorum/UtilityTopbar";
import { apiFetch, ApiError } from "@/lib/api";
import { socialMeta } from "@/lib/seo-meta";

export const Route = createFileRoute("/memo/$id/feedback")({
  head: ({ params }) => ({
    meta: [{ title: "Votre avis — Mémo Hōjin" }, { name: "robots", content: "noindex, nofollow" },
      ...socialMeta({
        title: "Votre avis — Mémo Hōjin",
        description: "Votre avis — Mémo Hōjin",
        url: `https://hojin.ai/memo/${params.id}/feedback`,
        image: "memo",
        card: "summary_large_image",
      }),
    ],
    links: [{ rel: "canonical", href: `https://hojin.ai/memo/${params.id}/feedback` }],
  }),
  component: FeedbackPage,
});

const PRICE_CHIPS = [9, 19, 29, 49, 79];

function FeedbackPage() {
  return (
    <ProtectedRoute>
      <FeedbackView />
    </ProtectedRoute>
  );
}

function FeedbackView() {
  const { id } = Route.useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [nps, setNps] = useState(8);
  const [price, setPrice] = useState<number>(29);
  const [customPrice, setCustomPrice] = useState(false);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  // Confirm ownership
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await apiFetch(`/api/upsell/secretaire/memo/${id}`);
        if (!cancelled) setLoading(false);
      } catch (e) {
        if (cancelled) return;
        const err = e as ApiError;
        if (err?.status === 404) setNotFound(true);
        setLoading(false);
      }
    })();
    // optional prefill — silent fallback
    (async () => {
      try {
        const prev = await apiFetch<{
          nps_score?: number;
          perceived_value_eur?: number;
          comment?: string;
        }>(`/api/feedback/memo/${id}`);
        if (cancelled || !prev) return;
        if (typeof prev.nps_score === "number") setNps(prev.nps_score);
        if (typeof prev.perceived_value_eur === "number") {
          setPrice(prev.perceived_value_eur);
          if (!PRICE_CHIPS.includes(prev.perceived_value_eur)) setCustomPrice(true);
        }
        if (prev.comment) setComment(prev.comment);
      } catch {
        /* silent */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sent || submitting) return;
    if (nps < 0 || nps > 10) return;
    if (price < 0 || price > 500) return;

    setSubmitting(true);
    try {
      await apiFetch(`/api/feedback/memo`, {
        method: "POST",
        body: JSON.stringify({
          memo_id: id,
          nps_score: nps,
          perceived_value_eur: price,
          ...(comment.trim() ? { comment: comment.trim() } : {}),
        }),
      });
      toast.success("Merci pour votre retour. Il sera précieux.");
      setSent(true);
      setTimeout(() => {
        navigate({ to: "/memo/$id", params: { id } });
      }, 2000);
    } catch (e) {
      const err = e as ApiError;
      if (err?.status === 404) {
        toast.error("Mémo introuvable. Êtes-vous connecté avec le bon compte ?");
      } else if (err?.status === 503) {
        toast.error(
          "Le service de feedback n'est pas encore actif. Réessayez dans quelques minutes.",
        );
      } else {
        toast.error("Impossible d'envoyer pour le moment. Réessayez.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: '"DM Mono", monospace',
    fontSize: 11,
    letterSpacing: 2,
    textTransform: "uppercase",
    color: "var(--leather)",
    marginBottom: 8,
  };

  if (notFound) {
    return (
      <div style={{ background: "var(--paper)", minHeight: "100vh" }}>
        <UtilityTopbar />
        <div style={{ maxWidth: 720, margin: "120px auto", textAlign: "center", padding: 32 }}>
          <h1
            style={{
              fontFamily: '"Fraunces", serif',
              fontSize: 32,
              color: "var(--leather)",
            }}
          >
            Mémo introuvable
          </h1>
          <p
            style={{
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              fontStyle: "italic",
              fontSize: 18,
              color: "var(--smoke)",
              marginTop: 12,
            }}
          >
            Vérifiez que vous êtes connecté avec le bon compte.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="memo-feedback-page"
      style={{ background: "var(--paper)", minHeight: "100vh", paddingBottom: 80 }}
    >
      <UtilityTopbar />

      <div style={{ maxWidth: 760, margin: "0 auto", padding: "24px 32px 0" }}>
        <Link
          to="/memo/$id"
          params={{ id }}
          style={{
            background: sent ? "var(--accent-cramoisi)" : "transparent",
            border: `1px solid ${sent ? "var(--accent-cramoisi)" : "var(--graphite)"}`,
            color: "var(--charcoal)",
            padding: "8px 14px",
            fontFamily: '"DM Mono", monospace',
            fontSize: 11,
            letterSpacing: 2,
            textTransform: "uppercase",
            textDecoration: "none",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            fontWeight: sent ? 700 : 400,
          }}
        >
          <ArrowLeft size={14} /> Retour au mémo
        </Link>

        <div
          role="note"
          style={{
            marginTop: 16,
            padding: "12px 16px",
            background: "rgba(245, 237, 216, 0.5)",
            border: "1px solid var(--leather)",
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontStyle: "italic",
            fontSize: 14,
            color: "var(--charcoal)",
          }}
        >
          🔬 Phase bêta — Le pricing final sera fixé sur la base de vos retours.
          Votre voix compte directement.
        </div>
      </div>

      <form
        onSubmit={onSubmit}
        style={{ maxWidth: 760, margin: "0 auto", padding: "32px 32px 0" }}
      >
        <div style={labelStyle}>§ Votre avis</div>
        <h1
          style={{
            fontFamily: '"Fraunces", serif',
            fontWeight: 900,
            fontSize: 36,
            color: "var(--leather)",
            margin: "0 0 8px",
            lineHeight: 1.15,
          }}
        >
          La parole vous revient
        </h1>
        <p
          style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontStyle: "italic",
            fontSize: 18,
            color: "var(--smoke)",
            margin: "0 0 16px",
          }}
        >
          Aidez-nous à finaliser ce livrable.
        </p>
        <p
          style={{
            fontFamily: '"DM Sans", sans-serif',
            fontSize: 15,
            lineHeight: 1.65,
            color: "var(--charcoal)",
            margin: "0 0 40px",
          }}
        >
          Vous venez de consulter votre mémo. Trois questions en 60 secondes pour nous
          aider à fixer le juste prix et à corriger ce qui doit l'être.
        </p>

        {/* Q1 NPS */}
        <section style={{ marginBottom: 40 }}>
          <h2
            style={{
              fontFamily: '"Fraunces", serif',
              fontSize: 18,
              fontWeight: 700,
              color: "var(--charcoal)",
              margin: "0 0 8px",
            }}
          >
            Question 1 — Recommandation
          </h2>
          <p
            style={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: 14,
              color: "var(--graphite)",
              margin: "0 0 16px",
            }}
          >
            Sur une échelle de 0 à 10, recommanderiez-vous le Mémo Hōjin à un autre fondateur ?
          </p>
          <input
            data-testid="nps-slider"
            type="range"
            min={0}
            max={10}
            step={1}
            value={nps}
            onChange={(e) => setNps(parseInt(e.target.value, 10))}
            disabled={sent}
            style={{ width: "100%", accentColor: "var(--leather)" }}
            aria-label="Note NPS"
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontFamily: '"DM Mono", monospace',
              fontSize: 11,
              color: "var(--smoke)",
              marginTop: 4,
            }}
          >
            <span>0</span>
            <span
              style={{
                fontSize: 20,
                color: "var(--leather)",
                fontFamily: '"Fraunces", serif',
                fontWeight: 700,
              }}
              data-testid="nps-value"
            >
              {nps}
            </span>
            <span>10</span>
          </div>
          <p
            style={{
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              fontStyle: "italic",
              fontSize: 13,
              color: "var(--smoke)",
              textAlign: "center",
              margin: "4px 0 0",
            }}
          >
            0 = pas du tout · 10 = absolument
          </p>
        </section>

        {/* Q2 Pricing */}
        <section style={{ marginBottom: 40 }}>
          <h2
            style={{
              fontFamily: '"Fraunces", serif',
              fontSize: 18,
              fontWeight: 700,
              color: "var(--charcoal)",
              margin: "0 0 8px",
            }}
          >
            Question 2 — Pricing perçu
          </h2>
          <p
            style={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: 14,
              color: "var(--graphite)",
              margin: "0 0 16px",
            }}
          >
            Combien seriez-vous prêt à payer pour ce mémo ?
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {PRICE_CHIPS.map((p) => {
              const active = !customPrice && price === p;
              return (
                <button
                  type="button"
                  key={p}
                  data-testid={`price-chip-${p}`}
                  onClick={() => {
                    setPrice(p);
                    setCustomPrice(false);
                  }}
                  disabled={sent}
                  style={{
                    padding: "10px 18px",
                    border: `1px solid ${active ? "var(--leather)" : "var(--stone-mid)"}`,
                    background: active ? "var(--leather)" : "transparent",
                    color: active ? "var(--paper)" : "var(--charcoal)",
                    fontFamily: '"DM Mono", monospace',
                    fontSize: 13,
                    letterSpacing: 1,
                    cursor: sent ? "not-allowed" : "pointer",
                    fontWeight: active ? 700 : 400,
                  }}
                >
                  {p}€
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => setCustomPrice(true)}
              disabled={sent}
              style={{
                padding: "10px 14px",
                border: "none",
                background: "transparent",
                color: "var(--leather)",
                fontFamily: '"DM Sans", sans-serif',
                fontSize: 13,
                textDecoration: "underline",
                cursor: sent ? "not-allowed" : "pointer",
              }}
            >
              Autre montant
            </button>
          </div>
          {customPrice && (
            <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8 }}>
              <input
                data-testid="price-custom"
                type="number"
                min={0}
                max={500}
                value={price}
                onChange={(e) =>
                  setPrice(Math.min(500, Math.max(0, parseInt(e.target.value || "0", 10))))
                }
                disabled={sent}
                style={{
                  width: 120,
                  padding: "10px 12px",
                  border: "1px solid var(--stone-mid)",
                  background: "var(--ivory)",
                  fontFamily: '"DM Mono", monospace',
                  fontSize: 14,
                  color: "var(--charcoal)",
                }}
                aria-label="Montant personnalisé en euros"
              />
              <span
                style={{
                  fontFamily: '"DM Mono", monospace',
                  fontSize: 13,
                  color: "var(--smoke)",
                }}
              >
                € (max 500)
              </span>
            </div>
          )}
        </section>

        {/* Q3 Comment */}
        <section style={{ marginBottom: 40 }}>
          <h2
            style={{
              fontFamily: '"Fraunces", serif',
              fontSize: 18,
              fontWeight: 700,
              color: "var(--charcoal)",
              margin: "0 0 8px",
            }}
          >
            Question 3 — Commentaire libre
            <span
              style={{
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontStyle: "italic",
                fontWeight: 400,
                fontSize: 14,
                color: "var(--smoke)",
                marginLeft: 8,
              }}
            >
              (optionnel)
            </span>
          </h2>
          <p
            style={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: 14,
              color: "var(--graphite)",
              margin: "0 0 12px",
            }}
          >
            Qu'auriez-vous aimé y trouver de plus ? De moins ? Qu'est-ce qui vous a marqué ?
          </p>
          <div style={{ position: "relative" }}>
            <textarea
              data-testid="comment-textarea"
              rows={4}
              maxLength={2000}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={sent}
              style={{
                width: "100%",
                padding: "12px 14px",
                border: "1px solid var(--stone-mid)",
                background: "var(--ivory)",
                fontFamily: '"DM Sans", sans-serif',
                fontSize: 14,
                lineHeight: 1.6,
                color: "var(--charcoal)",
                resize: "vertical",
              }}
              aria-label="Commentaire libre"
            />
            <div
              style={{
                position: "absolute",
                bottom: 8,
                right: 12,
                fontFamily: '"DM Mono", monospace',
                fontSize: 11,
                color: "var(--smoke)",
              }}
              data-testid="comment-counter"
            >
              {comment.length}/2000
            </div>
          </div>
        </section>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 12,
            paddingTop: 24,
            borderTop: "0.5px solid var(--stone-mid)",
          }}
        >
          <Link
            to="/memo/$id"
            params={{ id }}
            style={{
              padding: "12px 20px",
              border: "1px solid var(--graphite)",
              background: "transparent",
              color: "var(--charcoal)",
              fontFamily: '"DM Mono", monospace',
              fontSize: 12,
              letterSpacing: 2,
              textTransform: "uppercase",
              textDecoration: "none",
            }}
          >
            Annuler
          </Link>
          <button
            type="submit"
            data-testid="submit-feedback"
            disabled={submitting || sent || loading}
            style={{
              padding: "12px 24px",
              border: "1px solid var(--leather)",
              background: sent ? "var(--accent-cramoisi)" : "var(--leather)",
              color: sent ? "var(--charcoal)" : "var(--paper)",
              fontFamily: '"DM Mono", monospace',
              fontSize: 12,
              letterSpacing: 2,
              textTransform: "uppercase",
              fontWeight: 700,
              cursor: submitting || sent ? "default" : "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            {sent ? (
              <>
                <Check size={14} /> Envoyé
              </>
            ) : submitting ? (
              "Envoi…"
            ) : (
              "Envoyer →"
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default FeedbackPage;
